/**
 * General Settings API
 * Handles communication with /api/v1/dcs/apps endpoint
 */

import { BASE_AUTH_OFFICE_API } from '@/core/api-config';
import { TokenManager } from '@/core/auth/token-manager';
import { ApiErrorMiddleware } from '@/core/api-error-middleware';
import { useOperatorStore } from '@/core/stores/operator-store';
import { CreateDcsAppRequest, DcsApp, DcsAppsListResponse, UpdateDcsAppRequest } from '../types/general-settings-types';

export class GeneralSettingsApi {
  constructor(
    private readonly baseUrl: string = BASE_AUTH_OFFICE_API,
    private readonly tokenManager: TokenManager
  ) {}

  /**
   * Fetch DCS Apps for current operator
   * GET /api/v1/dcs/apps?count=false&$filter=(operatorId eq {operatorId})
   * @returns Promise<DcsAppsListResponse>
   * @throws Error if API call fails
   */
  async getDcsApps(explicitOperatorId?: number): Promise<DcsAppsListResponse> {
    const operatorId = explicitOperatorId ?? useOperatorStore.getState().selectedOperator?.operatorId;

    if (!operatorId) {
      throw new Error('No operator selected. Please select an operator first.');
    }

    try {
      console.log('[GeneralSettingsApi] Fetching DCS Apps for operator:', operatorId);

      const headers = await this.tokenManager.getAuthHeaders();
      const headersWithOperator = {
        ...headers,
        'X-Operator-Id': operatorId.toString()
      };

      // Build OData filter query
      const filterQuery = encodeURIComponent(`(operatorId eq ${operatorId})`);
      const url = `${this.baseUrl}/dcs/apps?count=false&$filter=${filterQuery}`;

      const response = await ApiErrorMiddleware.fetchWithErrorHandling(
        url,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...headersWithOperator
          }
        },
        'General Settings API',
        '/dcs/apps'
      );

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      console.log('[GeneralSettingsApi] DCS Apps fetched successfully:', {
        operatorId,
        appCount: data.rows?.length || 0
      });

      return data as DcsAppsListResponse;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('[GeneralSettingsApi] Failed to fetch DCS Apps:', {
        operatorId,
        error: errorMessage
      });
      throw error;
    }
  }

  /**
   * Create a new DCS App for an operator
   * POST /api/v1/dcs/apps
   */
  async createDcsApp(request: CreateDcsAppRequest): Promise<DcsApp> {
    const operatorId = request.operatorId;

    const headers = await this.tokenManager.getAuthHeaders();
    const url = `${this.baseUrl}/dcs/apps`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Operator-Id': operatorId.toString(),
          ...headers,
        },
        body: JSON.stringify(request),
      },
      'General Settings API',
      '/dcs/apps'
    );

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

  // Some endpoints return 200 check error message
    if (data?.errorMessage) {
      throw new Error(data.errorMessage);
    }
    if (data?.isSuccess === false) {
      throw new Error(data?.message ?? 'Failed to create DCS app');
    }

    return data as DcsApp;
  }

  /**
   * Update DCS App settings
   * PATCH /api/v1/dcs/apps/{appId}
   * @param appId - The app ID to update
   * @param delta - Delta object with only changed fields
   * @returns Promise<void>
   * @throws Error if API call fails
   */
  async updateDcsApp(appId: number, delta: UpdateDcsAppRequest, explicitOperatorId?: number): Promise<void> {
    const operatorId = explicitOperatorId ?? useOperatorStore.getState().selectedOperator?.operatorId;

    if (!operatorId) {
      throw new Error('No operator selected. Please select an operator first.');
    }

    try {
      console.log('[GeneralSettingsApi] Updating DCS App:', {
        appId,
        operatorId,
        changedFields: Object.keys(delta).length
      });

      const headers = await this.tokenManager.getAuthHeaders();
      const headersWithOperator = {
        ...headers,
        'X-Operator-Id': operatorId.toString()
      };

      const url = `${this.baseUrl}/dcs/apps/${appId}`;

      const response = await ApiErrorMiddleware.fetchWithErrorHandling(
        url,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...headersWithOperator
          },
          body: JSON.stringify(delta)
        },
        'General Settings API',
        `/dcs/apps/${appId}`
      );

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      // Some endpoints return 200 check error message
      const data = await response.json().catch(() => null);
      if (data?.errorMessage) {
        throw new Error(data.errorMessage);
      }
      if (data?.isSuccess === false) {
        throw new Error(data?.message ?? 'Failed to update DCS app');
      }

      console.log('[GeneralSettingsApi] DCS App updated successfully:', {
        appId,
        operatorId
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('[GeneralSettingsApi] Failed to update DCS App:', {
        appId,
        operatorId,
        error: errorMessage
      });
      throw error;
    }
  }
}
